/**
 * Recommended products for this visitor, for the homepage "Gift ideas picked for you" rail.
 *
 * Product pages remember the last ten products viewed in localStorage (cc:recent: id, handle, title and
 * collection handles, set in theme.liquid; no personal data, never leaves the browser). Since 2026-09-30 the row
 * leads with what goes with the things in the visitor's bag and the last things they looked at: the complementary
 * products saved for each (pairings/, product-pairings skill), fetched from Shopify's recommendations service, taken
 * in turns. After those comes the older spread: the first recent view that maps into the hierarchy
 * (cc-taxonomy.json) picks a main category, and three random sibling departments fill the rest.
 *
 * window.ccRecommend() resolves to { personal: true, pool, from: 'pairs' | 'spread', label, main } for a visitor
 * with a bag or history, or null when there is none or anything fails - callers then keep their server-rendered
 * products. It is computed once per page, so every caller sees the same order.
 *
 * Pool items: { handle, type, title, url, price, img, variantId, single }
 */
(() => {
  const shuffle = (list) => {
    const a = list.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  // products.json gives image objects and variant objects; keep only what a
  // card needs, and only products that can actually be bought.
  const normalise = (p) => {
    const variants = p.variants || [];
    const v = variants.find((x) => x.available);
    const raw = p.images && p.images[0] ? (typeof p.images[0] === 'string' ? p.images[0] : p.images[0].src) : '';
    if (!v || !raw) return null;
    return {
      handle: p.handle,
      type: p.product_type || p.type || '',
      title: p.title,
      url: `/products/${p.handle}`,
      price: (typeof v.price === 'number' ? v.price / 100 : Number(v.price)).toFixed(2), // pence from .js/recommendations, pounds from products.json
      img: raw,
      variantId: v.id,
      single: variants.length === 1,
    };
  };

  const recentViews = () => {
    try {
      const list = JSON.parse(localStorage.getItem('cc:recent') || '[]');
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  };

  // What goes with a product: the complementary products saved for it (Shopify's Complementary products field,
  // filled offline by pairings/ and the product-pairings skill), through Shopify's recommendations service.
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const idFor = async (view) => {
    if (view.i) return view.i;
    const p = await fetch(`${root}products/${view.h}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    return p ? p.id : null;
  };
  const pairsFor = async (id) => {
    if (!id) return [];
    const j = await fetch(`${root}recommendations/products.json?product_id=${id}&intent=complementary&limit=10`)
      .then((r) => (r.ok ? r.json() : null)).catch(() => null);
    return (j && j.products) || [];
  };
  const bagItems = () => fetch(`${root}cart.js`).then((r) => r.json()).then((c) => c.items || []).catch(() => []);

  // The old spread: the first recent view that maps into the hierarchy picks a main category, and three random
  // sibling departments supply products. Now the filler after the pairings, and the whole row when there are none.
  const spread = async (recent, exclude) => {
    if (!recent.length || !window.ccTaxonomyUrl) return null;
    const tax = await fetch(window.ccTaxonomyUrl).then((r) => r.json()).catch(() => null);
    if (!tax) return null;
    let main = null;
    let seenSub = null;
    for (const view of recent) {
      for (const h of view.c || []) {
        if (tax.subToMain[h]) { seenSub = h; main = tax.subToMain[h]; break; }
      }
      if (main) break;
    }
    if (!main) return null;
    const siblings = (tax.mainToSubs[main] || []).filter((h) => h !== seenSub);
    if (!siblings.length) return null;
    const lists = await Promise.all(shuffle(siblings).slice(0, 3).map((h) =>
      fetch(`${root}collections/${h}/products.json?limit=24`).then((r) => r.json()).catch(() => null)));
    const pool = [];
    lists.forEach((list) => {
      (list && list.products ? list.products : []).forEach((p) => {
        if (exclude.has(p.handle)) return;
        const item = normalise(p);
        if (item) { exclude.add(p.handle); pool.push(item); }
      });
    });
    return { pool: shuffle(pool), label: tax.mainLabel[main] || '', main };
  };

  // For a visitor with a bag or recent views: first what goes with the things in their bag and the last things they
  // looked at (bag first, up to four products, taken in turns so no one product fills the row), then the spread.
  const compute = async () => {
    const recent = recentViews();
    const bag = await bagItems();
    if (!recent.length && !bag.length) return null;
    const sources = bag.map((it) => ({ i: it.product_id, h: it.handle }));
    for (const v of recent) {
      if (sources.length >= 4) break;
      if (!sources.some((s) => s.h === v.h)) sources.push(v);
    }
    const lists = await Promise.all(sources.slice(0, 4).map(async (s) => pairsFor(await idFor(s))));
    const exclude = new Set([...recent.map((v) => v.h), ...bag.map((it) => it.handle)]);
    const pairs = [];
    for (let k = 0; k < 10; k++) {
      lists.forEach((list) => {
        const p = list[k];
        if (!p || exclude.has(p.handle)) return;
        const item = normalise(p);
        if (item) { exclude.add(p.handle); pairs.push(item); }
      });
    }
    const rest = await spread(recent, exclude);
    const pool = [...pairs, ...(rest ? rest.pool : [])];
    if (pool.length < 6) return null;
    return { personal: true, pool, from: pairs.length ? 'pairs' : 'spread', label: rest ? rest.label : '', main: rest ? rest.main : null };
  };

  let cached = null;
  window.ccRecommend = () => {
    if (!cached) cached = compute().catch(() => null);
    return cached;
  };
  window.ccHasRecentViews = () => recentViews().length > 0;

  // The card markup, shared by everything that renders pool items in JS.
  // Mirrors snippets/cc-product-card.liquid and cc-add-button.liquid.
  const esc = (s) => String(s).replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch]);
  const ICON = '<span class="cc-add-icon" aria-hidden="true"><svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 8h14l-1.2 12.5H6.2L5 8z"/><path d="M9 8V6.5a3 3 0 0 1 6 0V8"/></svg></span>';

  // products.json urls take the _NNNx suffix; shop CDN urls carry width=.
  window.ccImg = (src, width) => (/[?&]width=\d+/.test(src)
    ? src.replace(/([?&]width=)\d+/, `$1${width}`)
    : src.replace(/(\.[a-z]+)(\?|$)/i, `_${width}x$1$2`));

  window.ccCard = (item) => {
    const add = item.single
      ? `<form class="cc-add" action="/cart/add" method="post"><input type="hidden" name="id" value="${item.variantId}"><input type="hidden" name="quantity" value="1"><button class="cc-add-btn" type="submit" aria-label="Add ${esc(item.title)} to cart">${ICON}<span class="cc-add-text"><span class="cc-add-label">Add</span><span class="cc-add-done" aria-hidden="true">Added</span></span></button></form>`
      : `<a class="cc-add-btn cc-add-btn--choose" href="${item.url}" aria-label="Choose options for ${esc(item.title)}">${ICON}<span class="cc-add-text"><span class="cc-add-label">Choose</span></span></a>`;
    return `<div class="cc-card">
      <div class="cc-shot"><img src="${window.ccImg(item.img, 480)}" alt="${esc(item.title)}" width="480" height="480" loading="lazy"></div>
      <p class="cc-title"><a class="cc-card-link" href="${item.url}">${esc(item.title)}</a></p>
      <div class="cc-buy"><span class="cc-price">£${item.price}</span>${add}</div>
    </div>`;
  };
})();
